/**
 * Абонентское обслуживание (ecology-subscription), подраздел «Экология» → «Абонентское обслуживание»
 * и плитка обзора. Клиент только читает: у абонента — блок «Сопровождение» (тариф, договор, инженер,
 * ближайший выезд и план работ на год с отметками «выполнено»), у остальных — предложение тарифов и
 * кнопка «Оставить заявку». Данные блока заполняет инженер Центрики в панели команды (макет клиента
 * 17.09, вариант Б; макет администратора 16.09).
 */
import { CalendarCheck2, ClipboardCheck, UserRound } from 'lucide-react'
import type { EcoPlanItem, EcoSubscription } from '@/lib/api'
import { formatEcoDate } from '@/lib/ecology'
import { Card } from '@/design-system/components/layout/card'
import { Badge } from '@/design-system/components/primitives/badge'
import { Button } from '@/design-system/components/primitives/button'

/** Тарифы для тех, кто не на сопровождении — тексты из макета клиента 17.09. */
const OFFER = [
  {
    title: 'Тариф «Защита»',
    visits: '2 выезда инженера в год',
    features: [
      'инвентаризация выбросов, программа ПЭК, постановка на учёт',
      'до 5 паспортов отходов, обучение одного сотрудника',
      'вся ежегодная отчётность и контроль сроков документов',
      'план работ на год и подготовка к проверкам',
    ],
  },
  {
    title: 'Тариф «Прайм»',
    visits: '4 выезда инженера в год',
    features: [
      'всё, что в тарифе «Защита»',
      'проекты НДВ и НМУ и их согласование',
      'документы вне пакета — по специальной цене',
    ],
  },
]

/** Подпись выездов по тарифу: 0 — не показываем. */
export function ecoVisitsLabel(visitsPerYear: number): string {
  if (visitsPerYear <= 0) return ''
  const rest = visitsPerYear % 100
  const last = visitsPerYear % 10
  const word =
    rest >= 11 && rest <= 14 ? 'выездов' : last === 1 ? 'выезд' : last < 5 ? 'выезда' : 'выездов'
  return `${visitsPerYear} ${word} в год`
}

function Stat({
  icon: Icon,
  label,
  value,
  hint,
}: {
  icon: typeof UserRound
  label: string
  value: string
  hint: string
}) {
  return (
    <div className="rounded-10 border border-line px-3 py-2.5">
      <div className="flex items-center gap-2 text-12 uppercase tracking-wide text-txt-tertiary">
        <Icon className="size-4 shrink-0 stroke-current" aria-hidden />
        {label}
      </div>
      <div className="mt-1.5 font-semibold text-txt">{value}</div>
      {hint ? <div className="mt-0.5 text-12 text-txt-secondary">{hint}</div> : null}
    </div>
  )
}

function PlanRow({ item }: { item: EcoPlanItem }) {
  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-line py-2.5 last:border-b-0">
      <span className="min-w-48 flex-1 text-txt">
        {item.title}
        {item.siteName ? (
          <span className="text-13 text-txt-secondary"> · {item.siteName}</span>
        ) : null}
      </span>
      <span className="text-13 text-txt-secondary">{item.dueText || '—'}</span>
      <Badge variant={item.done ? 'green' : 'gray'}>{item.done ? 'выполнено' : 'план'}</Badge>
    </li>
  )
}

/** Блок «Сопровождение» абонента. */
export function EcoSubscriptionCard({ subscription }: { subscription: EcoSubscription }) {
  const s = subscription
  const engineerContacts = [s.engineerPhone, s.engineerEmail].filter(Boolean).join(' · ')
  const visits = ecoVisitsLabel(s.visitsPerYear)
  // Клиент видит план текущего года; прошлые годы остаются историей у инженера.
  const plan = s.plan.filter((item) => item.year === s.planYear)
  const extras = [
    s.includesAirReport ? 'отчёт 2-ТП (воздух)' : '',
    s.includesWaterReport ? 'отчётность по воде' : '',
  ].filter(Boolean)
  return (
    <Card
      header={
        <span className="flex flex-wrap items-center justify-between gap-2">
          {`Сопровождение — тариф «${s.tariffLabel}»`}
          {s.contractUntil ? (
            <Badge variant="green">действует до {formatEcoDate(s.contractUntil)}</Badge>
          ) : null}
        </span>
      }
    >
      <p className="text-13 text-txt-secondary">
        Инженер-эколог Центрики ведёт вашу экологию: сроки, отчётность, проверки. Данные в этом блоке
        заполняет инженер.
      </p>
      <div className="mt-3 grid gap-3 nw:grid-cols-3">
        <Stat
          icon={UserRound}
          label="Ваш инженер"
          value={s.engineerName || 'назначается'}
          hint={engineerContacts}
        />
        <Stat
          icon={CalendarCheck2}
          label="Ближайший выезд"
          value={s.nextVisitOn ? formatEcoDate(s.nextVisitOn) : 'дата уточняется'}
          hint={[s.nextVisitNote, visits].filter(Boolean).join(' · ')}
        />
        <Stat
          icon={ClipboardCheck}
          label={`План работ ${s.planYear}`}
          value={`${s.planDone} из ${s.planTotal}`}
          hint="выполнено из запланированного"
        />
      </div>
      {extras.length > 0 ? (
        <p className="mt-3 text-13 text-txt-secondary">
          Дополнительно в сопровождении: {extras.join(', ')}.
        </p>
      ) : null}
      <h3 className="mb-1 mt-4 text-13 font-semibold uppercase tracking-wide text-txt-tertiary">
        План работ на {s.planYear} год
      </h3>
      {plan.length === 0 ? (
        <p className="text-13 text-txt-secondary">
          Инженер ещё не составил план работ — он появится здесь.
        </p>
      ) : (
        <ul>
          {plan.map((item) => (
            <PlanRow key={item.id} item={item} />
          ))}
        </ul>
      )}
    </Card>
  )
}

/** Предложение тарифов тем, кто не на сопровождении. */
export function EcoSubscriptionOffer({
  onRequest,
  requesting,
}: {
  /** «Оставить заявку» — заказ услуги «Абонентское обслуживание»; нет позиции в справочнике — кнопки нет. */
  onRequest?: () => void
  requesting?: boolean
}) {
  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-4 nw:grid-cols-2">
        {OFFER.map((tariff) => (
          <Card key={tariff.title} header={tariff.title}>
            <p className="text-13 text-txt-secondary">{tariff.visits}</p>
            <ul className="mt-2 flex list-disc flex-col gap-1 pl-5 text-txt">
              {tariff.features.map((f) => (
                <li key={f}>{f}</li>
              ))}
            </ul>
          </Card>
        ))}
      </div>
      <Card>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="font-semibold text-txt">
              Хотите, чтобы экологию вёл инженер Центрики?
            </div>
            <div className="text-13 text-txt-secondary">
              Менеджер рассчитает стоимость по вашим площадкам и свяжется с вами
            </div>
          </div>
          {onRequest ? (
            <Button disabled={requesting} onClick={onRequest}>
              Оставить заявку
            </Button>
          ) : null}
        </div>
      </Card>
    </div>
  )
}
